import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import CheckinCard from '@/components/feature/CheckinCard';
import PollWidget from '@/components/feature/PollWidget';
import TopicCard from '@/components/base/TopicCard';
import { useSeo } from '@/hooks/useSeo';
import { hotTopics, poll, topics } from '@/mocks/gossip';
import CommentSection from './components/CommentSection';

export default function TopicDetail() {
  const { id = '' } = useParams();
  const topic = topics.find((t) => t.id === id);

  const related = useMemo(
    () => topics.filter((t) => t.id !== id && t.category === topic?.category).slice(0, 3),
    [id, topic?.category],
  );

  const jsonLd = useMemo(() => {
    if (!topic) return undefined;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: topic.title,
        description: topic.excerpt,
        articleSection: topic.category,
        datePublished: topic.date,
        author: { '@type': 'Organization', name: topic.author },
        publisher: { '@type': 'Organization', name: '瓜田福利社' },
        keywords: topic.tags.join(','),
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: '吃瓜热榜', item: '/topics' },
          { '@type': 'ListItem', position: 3, name: topic.title, item: `/topic/${topic.id}` },
        ],
      },
    ];
  }, [topic]);

  useSeo({
    title: topic ? `${topic.title} - 福利吃瓜话题 - 瓜田福利社` : '话题未找到 - 瓜田福利社',
    description: topic
      ? `${topic.excerpt} 参与评论区打卡与粉丝投票，和其他瓜友一起讨论。`
      : '该话题可能已被移动或下线，返回吃瓜热榜查看更多内容。',
    keywords: topic ? `${topic.tags.join(',')},福利吃瓜,福利吃瓜评论区` : '福利吃瓜话题',
    path: `/topic/${id}`,
    jsonLd,
  });

  if (!topic) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="pt-16 md:pt-[68px]">
          <div className="w-full px-4 md:px-8 py-24 text-center">
            <i className="ri-emotion-unhappy-line text-5xl text-foreground-400"></i>
            <h1 className="mt-4 font-heading text-2xl text-foreground-950">没有找到这个话题</h1>
            <p className="mt-2 text-sm text-foreground-600">它可能已被移动或下线。</p>
            <Link
              to="/topics"
              className="mt-6 inline-flex px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              返回吃瓜热榜
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="pt-16 md:pt-[68px]">
        <div className="w-full px-4 md:px-8 pt-6 md:pt-8">
          <Breadcrumbs
            items={[
              { label: '首页', to: '/' },
              { label: '吃瓜热榜', to: '/topics' },
              { label: topic.category, to: `/topics?category=${encodeURIComponent(topic.category)}` },
              { label: topic.title },
            ]}
          />
        </div>

        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <div className="flex flex-col lg:flex-row gap-10">
            <article className="flex-1 min-w-0">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs">
                <i className="ri-price-tag-3-line"></i>
                {topic.category}
              </span>
              <h1 className="mt-4 font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
                {topic.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line"></i>
                  {topic.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {topic.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {topic.readCount.toLocaleString()} 次阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line text-primary-500"></i>
                  热度 {topic.heat}
                </span>
              </div>

              <div className="mt-6 w-full h-64 md:h-[420px] rounded-lg overflow-hidden border border-background-200">
                <img
                  src={topic.cover}
                  alt={`${topic.title} - 话题配图`}
                  title={`${topic.title} 福利吃瓜`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-8 space-y-5">
                {topic.content.map((paragraph, index) => (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-loose">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                {topic.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/topics?q=${encodeURIComponent(tag)}`}
                    className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-8 p-5 rounded-lg bg-background-100 border border-background-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-sm text-foreground-700">
                  喜欢这条内容？参与互动玩法，边吃瓜边拿福利。
                </p>
                <div className="flex gap-3 shrink-0">
                  <Link
                    to="/activities"
                    className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors"
                  >
                    去参与互动
                  </Link>
                  <a
                    href="#comments"
                    className="px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 text-sm whitespace-nowrap hover:border-primary-300 transition-colors"
                  >
                    写评论打卡
                  </a>
                </div>
              </div>

              <CommentSection topicId={topic.id} />
            </article>

            <aside className="w-full lg:w-80 shrink-0">
              <div className="space-y-6 lg:sticky lg:top-24">
                <CheckinCard />
                <PollWidget question={poll.question} options={poll.options} compact />

                <div className="rounded-lg bg-background-100 border border-background-200 p-5">
                  <h2 className="font-heading text-lg text-foreground-950 flex items-center gap-2">
                    <i className="ri-fire-line text-primary-500"></i>
                    热度榜
                  </h2>
                  <ol className="mt-4 space-y-3">
                    {hotTopics.slice(0, 5).map((item) => (
                      <li key={item.id} className="flex items-start gap-3">
                        <span
                          className={`shrink-0 w-6 h-6 flex items-center justify-center rounded text-xs font-bold ${
                            item.hot <= 3 ? 'bg-accent-500 text-foreground-950' : 'bg-background-300 text-foreground-700'
                          }`}
                        >
                          {item.hot}
                        </span>
                        <Link
                          to={`/topic/${item.id}`}
                          className="text-sm text-foreground-800 leading-snug hover:text-primary-600 transition-colors"
                        >
                          {item.title}
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </aside>
          </div>

          {related.length > 0 && (
            <section className="mt-14 pt-10 border-t border-background-200">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">
                相关话题推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {related.map((item) => (
                  <TopicCard key={item.id} topic={item} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}